import {
  ToastDuration,
  ToastElement,
  ToastMessage,
  ToastSensitivity,
  ToastType,
} from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  FEATURE_CLASS,
  GA4FeatureUseEvent,
  GA4FeatureUseEventProps,
  REQUIRED_USER_STATUS,
} from '@jusbrasil-web/shared-ga4-events'
import { ReactNode, RefObject } from 'react'

import { DocSearchElement } from '../components'
import {
  UseDeleteDocumentReturn,
  UseDocumentModerationModalReturn,
  UseDocumentReportModalReturn,
} from '../hooks'

type ToastPushOptions = {
  id?: string
  icon?: ReactNode
  duration?: ToastDuration
  type?: ToastType
  sensitivity?: ToastSensitivity
  ref?: RefObject<ToastElement>
}

type DocumentProps = {
  docId: number
  artifact: string
}

export type UseToolbarProps = {
  document: DocumentProps
  docSearchRef?: RefObject<DocSearchElement>
  sendMetrics: (name: string, properties?: Record<string, unknown>) => void
  toast?: (message: ToastMessage, options?: ToastPushOptions) => void
  deleteDocument?: UseDeleteDocumentReturn
  setDeleteConfirmOpened?: (value: boolean) => void
  documentModeration?: UseDocumentModerationModalReturn
  documentReport?: UseDocumentReportModalReturn
  featureName?: string | null
  componentClass?: string
}

export type UseToolbarReturn = {
  onSearchButtonClick: () => void
  onPrint: () => void
  onReport: () => void
  onDelete: () => Promise<void>
  onModeration: () => void
}

export function useToolbar(props: UseToolbarProps): UseToolbarReturn {
  const {
    document,
    docSearchRef,
    toast,
    deleteDocument,
    setDeleteConfirmOpened,
    documentModeration,
    documentReport,
    sendMetrics,
    featureName,
    componentClass = '',
  } = props
  const { artifact, docId } = document

  const { sendGA4Event } = useGA4Dispatcher()

  const onSearchButtonClick = () => {
    docSearchRef?.current?.toggle()
    const searchButtonClickEventData: GA4FeatureUseEventProps = {
      featureClass: FEATURE_CLASS.DOCUMENT_SEARCH,
      featureName,
      componentClass,
      resourceId: `${artifact}:${docId}`,
      requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
      successfulAttempt: true,
    }
    sendGA4Event(new GA4FeatureUseEvent(searchButtonClickEventData))

    sendMetrics('Doc.InnerSearchButtonClicked', {
      metadata: {
        activation_method: 'click',
      },
    })
  }

  const onPrint = () => {
    setTimeout(() => {
      window.print()
    }, 100)
    sendMetrics('Doc.PrintClicked')
  }

  const onReport = () => {
    sendMetrics('Doc.ReportClicked')
    documentReport?.open({
      docId: document.docId,
      artifact: document.artifact,
    })
  }

  const onDelete = async () => {
    await deleteDocument
      ?.deleteDocument()
      .then(() => {
        toast?.('Documento deletado.')
        setTimeout(() => {
          window.location.href = '/'
        }, 1500)
      })
      .catch(() => toast?.('Não foi possível deletar o documento.', { type: 'negative' }))
      .finally(() => setDeleteConfirmOpened?.(false))
  }

  const onModeration = () =>
    documentModeration?.open({
      docId: document.docId,
      artifact: document.artifact,
    })

  return {
    onSearchButtonClick,
    onPrint,
    onReport,
    onDelete,
    onModeration,
  }
}
